import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { VoiceMode } from '@/components/voice/VoiceMode';
import { ProcessDiagram } from '@/components/diagram/ProcessDiagram';
import { SIPOCTable } from '@/components/sipoc/SIPOCTable';
import { usePubSub } from '@/hooks/usePubSub';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { useAuth } from '@/contexts/AuthContext';
import { exportSIPOCToPDF, exportProcessMapToPDF } from '@/lib/pdfExport';
import type { ProcessGraph, SIPOCData, VoiceMessage, DiscoverySession } from '@acde/shared';

type ViewMode = 'diagram' | 'sipoc' | 'split';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Extract template ID from session ID (format: templateId-uuid)
function extractTemplateId(sessionId: string): string {
  const parts = sessionId.split('-');
  if (parts.length > 1) {
    // Handle template IDs like "workflow-discovery" or "ai-adoption"
    const templateParts = [];
    for (const part of parts) {
      // UUID parts are 8-4-4-4-12 chars, template parts are words
      if (part.length === 8 || part.length === 4 || part.length === 12) {
        break;
      }
      templateParts.push(part);
    }
    return templateParts.join('-') || 'workflow-discovery';
  }
  return 'workflow-discovery';
}

export function InterviewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { authFetch } = useAuthFetch();
  const { isReady } = useAuth();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<DiscoverySession | null>(null);

  // Graph and SIPOC state
  const [graph, setGraph] = useState<ProcessGraph>({
    sessionId: sessionId || '',
    nodes: [],
    edges: [],
    lastUpdated: new Date().toISOString(),
    version: 1,
  });

  const [sipoc, setSipoc] = useState<SIPOCData>({
    sessionId: sessionId || '',
    suppliers: [],
    inputs: [],
    processSummary: '',
    processSteps: [],
    outputs: [],
    customers: [],
    lastUpdated: new Date().toISOString(),
  });

  const [messages, setMessages] = useState<VoiceMessage[]>([]);
  const [currentPhase, setCurrentPhase] = useState<string>('start');
  const [lastSavedMessageCount, setLastSavedMessageCount] = useState(0);
  const [lastAnalyzedMessageCount, setLastAnalyzedMessageCount] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const analyzeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAutoStartedVoiceRef = useRef(false);

  // Check if we should auto-start voice mode (coming from dashboard with autoStartVoice flag)
  const shouldAutoStartVoice = (location.state as { autoStartVoice?: boolean })?.autoStartVoice === true;

  // Memoize callbacks for PubSub to prevent unnecessary reconnections
  const handleGraphUpdate = useCallback((updatedGraph: ProcessGraph) => {
    console.log('[Interview] Graph update received:', updatedGraph.nodes.length, 'nodes');
    setGraph(updatedGraph);
  }, []);

  const handleSIPOCUpdate = useCallback((updatedSipoc: SIPOCData) => {
    console.log('[Interview] SIPOC update received');
    setSipoc(updatedSipoc);
  }, []);

  const handlePhaseChange = useCallback((phase: string) => {
    console.log('[Interview] Phase change:', phase);
    setCurrentPhase(phase);
  }, []);

  const handleSessionStatus = useCallback((data: { status: string; metadata?: { processName?: string; completionPercentage?: number } }) => {
    console.log('[Interview] Session status update:', data);
    if (data.metadata) {
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          metadata: {
            ...prev.metadata,
            ...data.metadata,
          },
        };
      });
    }
  }, []);

  // Connect to PubSub for real-time updates
  const pubSubOptions = useMemo(() => ({
    sessionId: sessionId || '',
    onGraphUpdate: handleGraphUpdate,
    onSIPOCUpdate: handleSIPOCUpdate,
    onPhaseChange: handlePhaseChange,
    onSessionStatus: handleSessionStatus,
  }), [sessionId, handleGraphUpdate, handleSIPOCUpdate, handlePhaseChange, handleSessionStatus]);

  const { isConnected: isPubSubConnected, error: pubSubError } = usePubSub(pubSubOptions);

  // Analyze transcript to extract graph, SIPOC, and phase
  const analyzeSession = useCallback(async () => {
    if (!sessionId || isAnalyzing) return;

    setIsAnalyzing(true);
    try {
      console.log('[Interview] Triggering analysis...');
      const response = await authFetch(`${API_URL}/sessions/${sessionId}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[Interview] Analysis complete:', result.analysis);
        
        // Update local state with the analysis results
        if (result.session) {
          const updatedSession = result.session as DiscoverySession;
          setSession(updatedSession);
          setGraph(updatedSession.graph);
          setSipoc(updatedSession.sipoc);
          setCurrentPhase(updatedSession.currentPhase);
        }
        
        setLastAnalyzedMessageCount(messages.length);
      } else {
        const errorData = await response.json();
        console.error('[Interview] Analysis failed:', errorData);
      }
    } catch (err) {
      console.error('[Interview] Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  }, [sessionId, isAnalyzing, authFetch, messages.length]);

  // Debounced analysis trigger - analyze when messages accumulate
  useEffect(() => {
    // Only analyze if we have new messages since last analysis
    const newMessageCount = messages.length - lastAnalyzedMessageCount;
    
    // Trigger analysis every 4 new messages or when conversation has 6+ messages
    if (newMessageCount >= 4 || (messages.length >= 6 && lastAnalyzedMessageCount === 0)) {
      // Clear any pending timeout
      if (analyzeTimeoutRef.current) {
        clearTimeout(analyzeTimeoutRef.current);
      }
      
      // Debounce the analysis call
      analyzeTimeoutRef.current = setTimeout(() => {
        analyzeSession();
      }, 2000); // Wait 2 seconds after last message before analyzing
    }

    return () => {
      if (analyzeTimeoutRef.current) {
        clearTimeout(analyzeTimeoutRef.current);
      }
    };
  }, [messages.length, lastAnalyzedMessageCount, analyzeSession]);

  // Fetch or create session on mount
  useEffect(() => {
    if (!sessionId || !isReady) return;

    const initializeSession = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // First, try to fetch existing session
        const getResponse = await authFetch(`${API_URL}/sessions/${sessionId}`);

        if (getResponse.ok) {
          // Session exists, load it
          const data = await getResponse.json();
          const existingSession = data.session as DiscoverySession;

          setSession(existingSession);
          setGraph(existingSession.graph);
          setSipoc(existingSession.sipoc);
          setCurrentPhase(existingSession.currentPhase);

          // Convert transcript entries to voice messages for display
          console.log('[Interview] Session transcript from API:', existingSession.transcript);
          if (existingSession.transcript && existingSession.transcript.length > 0) {
            const loadedMessages = existingSession.transcript.map((entry) => ({
              id: entry.id,
              role: entry.role as 'user' | 'assistant',
              content: entry.content,
              timestamp: new Date(entry.timestamp),
              isFinal: true,
            }));
            console.log('[Interview] Converted messages:', loadedMessages);
            setMessages(loadedMessages);
            // Mark these as already saved so we don't re-save them
            setLastSavedMessageCount(loadedMessages.length);
          } else {
            console.log('[Interview] No transcript found in session');
          }

          console.log('[Interview] Loaded existing session:', sessionId, 'with', existingSession.transcript?.length || 0, 'transcript entries');
        } else if (getResponse.status === 404) {
          // Session doesn't exist, create it
          const templateId = (location.state as { template?: string })?.template || extractTemplateId(sessionId);

          const createResponse = await authFetch(`${API_URL}/sessions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              templateId,
              metadata: {
                processName: '',
              },
            }),
          });

          if (!createResponse.ok) {
            const errorData = await createResponse.json();
            throw new Error(errorData.error || 'Failed to create session');
          }

          const data = await createResponse.json();
          const newSession = data.session as DiscoverySession;

          setSession(newSession);
          setGraph(newSession.graph);
          setSipoc(newSession.sipoc);
          setCurrentPhase(newSession.currentPhase);

          console.log('[Interview] Created new session:', newSession.id);

          // Update URL to use the actual session ID from the server
          if (newSession.id !== sessionId) {
            navigate(`/interview/${newSession.id}`, { replace: true });
          }
        } else {
          const errorData = await getResponse.json();
          throw new Error(errorData.error || 'Failed to load session');
        }
      } catch (err) {
        console.error('[Interview] Session initialization error:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize session');
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, [sessionId, isReady, authFetch, navigate, location.state]);

  // Auto-start voice mode if coming from dashboard with autoStartVoice flag
  useEffect(() => {
    if (shouldAutoStartVoice && !isLoading && !error && session && !hasAutoStartedVoiceRef.current) {
      hasAutoStartedVoiceRef.current = true;
      // Small delay to ensure everything is ready
      setTimeout(() => {
        setIsVoiceOpen(true);
      }, 300);
    }
  }, [shouldAutoStartVoice, isLoading, error, session]);

  // Handle voice messages
  const handleVoiceMessage = useCallback((message: VoiceMessage) => {
    setMessages((prev) => {
      // Prevent duplicate messages
      if (prev.some((m) => m.id === message.id)) {
        return prev;
      }
      return [...prev, message];
    });
  }, []);

  // Save transcript to API
  const saveTranscript = useCallback(async (messagesToSave: VoiceMessage[]) => {
    if (!sessionId || messagesToSave.length === 0) return;

    try {
      // Convert VoiceMessage to TranscriptEntry format
      const transcriptEntries = messagesToSave.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : String(msg.timestamp),
        phase: currentPhase,
      }));

      console.log('[Interview] Saving transcript entries:', transcriptEntries);

      const response = await authFetch(`${API_URL}/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appendTranscript: transcriptEntries,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[Interview] Transcript saved successfully:', transcriptEntries.length, 'entries, session now has', result.session?.transcript?.length || 0, 'total');
      } else {
        const errorData = await response.json();
        console.error('[Interview] Failed to save transcript:', errorData);
      }
    } catch (err) {
      console.error('[Interview] Failed to save transcript:', err);
    }
  }, [sessionId, currentPhase, authFetch]);

  // Auto-save transcript when new messages arrive (every 3 new messages)
  useEffect(() => {
    const unsavedMessages = messages.slice(lastSavedMessageCount);
    if (unsavedMessages.length >= 3) {
      saveTranscript(unsavedMessages).then(() => {
        setLastSavedMessageCount(messages.length);
      });
    }
  }, [messages, lastSavedMessageCount, saveTranscript]);

  // Save transcript when voice mode closes and trigger analysis
  const handleVoiceModeClose = useCallback(async () => {
    // Save any unsaved messages before closing
    const unsavedMessages = messages.slice(lastSavedMessageCount);
    if (unsavedMessages.length > 0) {
      await saveTranscript(unsavedMessages);
      setLastSavedMessageCount(messages.length);
    }
    setIsVoiceOpen(false);
    
    // Trigger analysis after closing if we have enough messages
    if (messages.length >= 4 && messages.length > lastAnalyzedMessageCount) {
      // Small delay to ensure transcript is saved first
      setTimeout(() => {
        analyzeSession();
      }, 1000);
    }
  }, [messages, lastSavedMessageCount, saveTranscript, lastAnalyzedMessageCount, analyzeSession]);

  // Handle export
  const handleExport = useCallback(async (format: 'json' | 'mermaid' | 'sipoc-csv' | 'transcript-csv') => {
    if (!sessionId) return;

    try {
      const response = await authFetch(`${API_URL}/sessions/${sessionId}/export?format=${format}`);

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `export-${sessionId}.${format === 'json' ? 'json' : format === 'mermaid' ? 'mmd' : 'csv'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('[Interview] Export error:', err);
      setError('Failed to export session');
    }
  }, [sessionId, authFetch]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted">Loading session...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Session Error
          </h3>
          <p className="text-muted mb-4">{error}</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 text-sm text-foreground bg-user-msg rounded-lg hover:bg-border transition-colors"
            >
              Go Home
            </button>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header with session info */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* View mode buttons */}
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1 sm:pb-0">
            <button
              onClick={() => setViewMode('diagram')}
              className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                viewMode === 'diagram'
                  ? 'bg-primary text-white'
                  : 'bg-user-msg text-foreground hover:bg-border'
              }`}
            >
              <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="hidden sm:inline">Process Map</span>
              <span className="sm:hidden">Map</span>
            </button>
            <button
              onClick={() => setViewMode('sipoc')}
              className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                viewMode === 'sipoc'
                  ? 'bg-primary text-white'
                  : 'bg-user-msg text-foreground hover:bg-border'
              }`}
            >
              <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              SIPOC
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors whitespace-nowrap hidden md:flex items-center gap-1.5 ${
                viewMode === 'split'
                  ? 'bg-primary text-white'
                  : 'bg-user-msg text-foreground hover:bg-border'
              }`}
            >
              Split View
            </button>
          </div>

          {/* Connection status */}
          <div className="flex items-center gap-2 text-xs">
            <span
              className={`w-2 h-2 rounded-full ${
                isPubSubConnected ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'
              }`}
            />
            <span className="text-muted">
              {isPubSubConnected ? 'Live' : 'Connecting...'}
            </span>
            {pubSubError && (
              <span className="text-red-500" title={pubSubError}>
                ⚠
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Export dropdown */}
          <div className="relative group">
            <button className="p-2 sm:px-3 sm:py-1.5 text-sm rounded-lg bg-user-msg text-foreground hover:bg-border transition-colors flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="hidden sm:inline">Export</span>
            </button>
            <div className="absolute right-0 mt-1 w-52 py-1 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <div className="px-3 py-1 text-xs text-muted font-medium">PDF Exports</div>
              <button
                onClick={() => exportSIPOCToPDF(sipoc, { processName: session?.metadata?.processName, sessionId: session?.id })}
                className="w-full px-4 py-2 text-left text-sm hover:bg-user-msg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 13h1v4h-1v-4zm2.5 0h1.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5H12v1h-1v-4zm4 0h2v1h-1v.5h1v1h-1v1.5h-1v-4zm-4 1v1h.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5H11z"/>
                </svg>
                SIPOC Analysis (PDF)
              </button>
              <button
                onClick={() => exportProcessMapToPDF(graph, { processName: session?.metadata?.processName, sessionId: session?.id })}
                className="w-full px-4 py-2 text-left text-sm hover:bg-user-msg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 13h1v4h-1v-4zm2.5 0h1.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5H12v1h-1v-4zm4 0h2v1h-1v.5h1v1h-1v1.5h-1v-4zm-4 1v1h.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5H11z"/>
                </svg>
                Process Map (PDF)
              </button>
              <div className="border-t border-border my-1"></div>
              <div className="px-3 py-1 text-xs text-muted font-medium">Other Formats</div>
              <button
                onClick={() => handleExport('json')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-user-msg transition-colors"
              >
                Full Session (JSON)
              </button>
              <button
                onClick={() => handleExport('mermaid')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-user-msg transition-colors"
              >
                Flowchart (Mermaid)
              </button>
              <button
                onClick={() => handleExport('sipoc-csv')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-user-msg transition-colors"
              >
                SIPOC Data (CSV)
              </button>
              <button
                onClick={() => handleExport('transcript-csv')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-user-msg transition-colors"
              >
                Transcript (CSV)
              </button>
            </div>
          </div>

          {/* Voice button */}
          <button
            onClick={() => setIsVoiceOpen(true)}
            className="btn btn-primary flex items-center gap-2 text-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            <span className="hidden sm:inline">{session?.status === 'active' ? 'Continue Interview' : 'Start Interview'}</span>
            <span className="sm:hidden">Interview</span>
          </button>
        </div>
      </div>

      {/* Session info bar */}
      {session && (
        <div className="mb-4 px-3 sm:px-4 py-2 bg-user-msg rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <span className="text-muted">
              Phase: <span className="text-foreground font-medium capitalize">{currentPhase}</span>
            </span>
            {session.metadata?.processName ? (
              <span className="text-muted truncate max-w-[200px] sm:max-w-none">
                Process: <span className="text-foreground font-medium">{session.metadata.processName}</span>
              </span>
            ) : messages.length > 0 && (
              <span className="text-muted italic text-xs">
                Detecting process...
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {isAnalyzing && (
              <span className="flex items-center gap-1.5 text-primary">
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="hidden sm:inline">Analyzing...</span>
              </span>
            )}
            <span className="text-muted whitespace-nowrap">
              {graph.nodes.length} steps • {sipoc.suppliers.length + sipoc.inputs.length + sipoc.outputs.length + sipoc.customers.length} SIPOC
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`flex-1 grid gap-3 sm:gap-4 ${
          viewMode === 'split' 
            ? 'grid-cols-1 md:grid-cols-2' 
            : 'grid-cols-1'
        }`}
      >
        {(viewMode === 'diagram' || viewMode === 'split') && (
          <div className="card min-h-[300px] md:h-full overflow-hidden">
            <ProcessDiagram graph={graph} isAnalyzing={isAnalyzing} />
          </div>
        )}
        {(viewMode === 'sipoc' || viewMode === 'split') && (
          <div className="card min-h-[300px] md:h-full overflow-hidden">
            <SIPOCTable 
              data={sipoc} 
              isAnalyzing={isAnalyzing} 
              processName={session?.metadata?.processName}
              sessionId={session?.id}
            />
          </div>
        )}
      </div>

      {/* Voice Mode Modal */}
      <VoiceMode
        isOpen={isVoiceOpen}
        onClose={handleVoiceModeClose}
        sessionId={session?.id || sessionId}
        onMessage={handleVoiceMessage}
        previousMessages={messages}
        processName={session?.metadata?.processName}
        sipocData={sipoc}
        currentPhase={currentPhase}
        completionPercentage={session?.metadata?.completionPercentage}
      />
    </div>
  );
}
